import { useState } from 'react';
import { type CareTeamMember } from '../appTypes';
import { apiGet, API_BASE, fetchWithRetry } from '../appUtils';
import { enqueue, isOfflineable } from '../offlineQueue';
import { toast } from 'sonner';

export function useCareTeam(authToken: string | null) {
  const [careTeamExpanded, setCareTeamExpanded] = useState<number | null>(null);
  const [careTeamDownloading, setCareTeamDownloading] = useState<Record<number, boolean>>({});
  const [careTeamMembers, setCareTeamMembers] = useState<CareTeamMember[]>([]);
  const [careTeamLoading, setCareTeamLoading] = useState(false);
  const [showCareTeamForm, setShowCareTeamForm] = useState(false);
  const [careTeamFormMode, setCareTeamFormMode] = useState<'add' | 'edit'>('add');
  const [careTeamEditTarget, setCareTeamEditTarget] = useState<CareTeamMember | null>(null);
  const [careTeamFormName, setCareTeamFormName] = useState('');
  const [careTeamFormRole, setCareTeamFormRole] = useState('');
  const [careTeamFormOrg, setCareTeamFormOrg] = useState('');
  const [careTeamFormPhone, setCareTeamFormPhone] = useState('');
  const [careTeamFormNotes, setCareTeamFormNotes] = useState('');
  const [careTeamFormAppt, setCareTeamFormAppt] = useState('');
  const [careTeamFormEmoji, setCareTeamFormEmoji] = useState('🩺');
  const [careTeamFormColor, setCareTeamFormColor] = useState('#8b6fb0');
  const [careTeamFormMemberType, setCareTeamFormMemberType] = useState<'provider' | 'administrator'>('provider');
  const [careTeamFormEmail, setCareTeamFormEmail] = useState('');
  const [careTeamFormSaving, setCareTeamFormSaving] = useState(false);

  async function loadCareTeam() {
    if (!authToken) return;
    setCareTeamLoading(true);
    try {
      const remote = await apiGet<CareTeamMember[]>('/care-team', authToken);
      if (remote) setCareTeamMembers(remote);
    } finally {
      setCareTeamLoading(false);
    }
  }

  async function saveCareTeamMember() {
    if (!careTeamFormName.trim()) return;
    setCareTeamFormSaving(true);
    try {
      const body = {
        name: careTeamFormName.trim(),
        role: careTeamFormRole.trim() || undefined,
        organisation: careTeamFormOrg.trim() || undefined,
        phone: careTeamFormPhone.trim() || undefined,
        email: careTeamFormEmail.trim() || undefined,
        notes: careTeamFormNotes.trim() || undefined,
        nextAppointment: careTeamFormAppt.trim() || undefined,
        emoji: careTeamFormEmoji,
        color: careTeamFormColor,
        memberType: careTeamFormMemberType,
      };
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      if (careTeamFormMode === 'add') {
        try {
          const res = await fetchWithRetry(`${API_BASE}/care-team`, { method: 'POST', headers, body: JSON.stringify(body) });
          if (res.ok) {
            const inserted = await res.json() as CareTeamMember;
            setCareTeamMembers(prev => [inserted, ...prev]);
          } else {
            toast.error("Couldn't save. Try again.");
          }
        } catch {
          if (isOfflineable('/api/care-team')) {
            enqueue({ method: 'POST', path: `${API_BASE}/care-team`, body, token: authToken ?? null });
            toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
          } else {
            toast.error("Couldn't reach the server. This change wasn't saved — try again when you have a better connection.");
          }
        }
      } else if (careTeamEditTarget) {
        const editPath = `/api/care-team/${careTeamEditTarget.id}`;
        try {
          const res = await fetchWithRetry(`${API_BASE}/care-team/${careTeamEditTarget.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
          if (res.ok) {
            const updated = await res.json() as CareTeamMember;
            setCareTeamMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
          } else {
            toast.error("Couldn't save. Try again.");
          }
        } catch {
          if (isOfflineable(editPath)) {
            enqueue({ method: 'PUT', path: `${API_BASE}/care-team/${careTeamEditTarget.id}`, body, token: authToken ?? null });
            toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
          } else {
            toast.error("Couldn't reach the server. This change wasn't saved — try again when you have a better connection.");
          }
        }
      }
      setShowCareTeamForm(false);
      resetCareTeamForm();
    } finally {
      setCareTeamFormSaving(false);
    }
  }

  async function deleteCareTeamMember(id: number) {
    const headers: HeadersInit = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    try {
      const res = await fetchWithRetry(`${API_BASE}/care-team/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setCareTeamMembers(prev => prev.filter(m => m.id !== id));
        if (careTeamExpanded !== null) setCareTeamExpanded(null);
      } else {
        toast.error("Couldn't remove. Try again.");
      }
    } catch {
      toast.error("Couldn't reach the server. Check your connection and try again.");
    }
  }

  function resetCareTeamForm() {
    setCareTeamFormName('');
    setCareTeamFormRole('');
    setCareTeamFormOrg('');
    setCareTeamFormPhone('');
    setCareTeamFormEmail('');
    setCareTeamFormNotes('');
    setCareTeamFormAppt('');
    setCareTeamFormEmoji('🩺');
    setCareTeamFormColor('#8b6fb0');
    setCareTeamFormMemberType('provider');
    setCareTeamEditTarget(null);
    setCareTeamFormMode('add');
  }

  function openCareTeamEditForm(member: CareTeamMember) {
    setCareTeamFormMode('edit');
    setCareTeamEditTarget(member);
    setCareTeamFormName(member.name);
    setCareTeamFormRole(member.role ?? '');
    setCareTeamFormOrg(member.organisation ?? '');
    setCareTeamFormPhone(member.phone ?? '');
    setCareTeamFormEmail(member.email ?? '');
    setCareTeamFormNotes(member.notes ?? '');
    setCareTeamFormAppt(member.nextAppointment ?? '');
    setCareTeamFormEmoji(member.emoji);
    setCareTeamFormColor(member.color);
    setCareTeamFormMemberType((member.memberType === 'administrator' ? 'administrator' : 'provider'));
    setShowCareTeamForm(true);
  }

  return {
    careTeamExpanded,
    setCareTeamExpanded,
    careTeamDownloading,
    setCareTeamDownloading,
    careTeamMembers,
    setCareTeamMembers,
    careTeamLoading,
    showCareTeamForm,
    setShowCareTeamForm,
    careTeamFormMode,
    careTeamEditTarget,
    careTeamFormName,
    setCareTeamFormName,
    careTeamFormRole,
    setCareTeamFormRole,
    careTeamFormOrg,
    setCareTeamFormOrg,
    careTeamFormPhone,
    setCareTeamFormPhone,
    careTeamFormNotes,
    setCareTeamFormNotes,
    careTeamFormAppt,
    setCareTeamFormAppt,
    careTeamFormEmoji,
    setCareTeamFormEmoji,
    careTeamFormColor,
    setCareTeamFormColor,
    careTeamFormMemberType,
    setCareTeamFormMemberType,
    careTeamFormEmail,
    setCareTeamFormEmail,
    careTeamFormSaving,
    loadCareTeam,
    saveCareTeamMember,
    deleteCareTeamMember,
    resetCareTeamForm,
    openCareTeamEditForm,
  };
}
